import type { EventBus } from '@/core/EventBus';
import type { GameController } from '@/game/GameController';
import { NetworkEvents } from '@/network/NetworkManager';
import type { PlayerService } from '@/player/PlayerService';
import { PlayerEvents } from '@/player/PlayerService';
import type { PlayerNameModal } from './PlayerNameModal';

const QUEUE_TIMEOUT_MS = 30000; // 30 seconds before falling back to AI

/**
 * BattleLobbyPanel - Main menu with Play vs AI and Find Match options
 */

export interface BattleLobbyPanelOptions {
  onEnterBattle?: () => void;
}

export class BattleLobbyPanel {
  private container: HTMLElement;
  private element: HTMLElement | null = null;
  private eventBus: EventBus | null = null;
  private gameController: GameController | null = null;
  private playerService: PlayerService | null = null;
  private playerNameModal: PlayerNameModal | null = null;
  private onEnterBattle: () => void;
  private queueTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(container: HTMLElement, options: BattleLobbyPanelOptions = {}) {
    this.container = container;
    this.onEnterBattle = options.onEnterBattle || (() => {});
    this.createElement();
    this.hide();
  }

  /**
   * Set event bus, game controller, and player service for full functionality
   */
  setGameContext(
    eventBus: EventBus,
    gameController: GameController,
    playerService?: PlayerService,
    playerNameModal?: PlayerNameModal
  ): void {
    this.eventBus = eventBus;
    this.gameController = gameController;
    this.playerService = playerService || null;
    this.playerNameModal = playerNameModal || null;
    this.setupNetworkListeners();
    this.updateGreeting();
  }

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.id = 'battle-lobby-panel';
    this.element.innerHTML = `
      <style>
        #battle-lobby-panel {
          position: fixed;
          inset: 0;
          background: rgba(10, 12, 18, 0.98);
          z-index: 9000;
          font-family: system-ui, -apple-system, sans-serif;
          color: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding-bottom: 80px;
        }

        .blp-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          text-align: center;
          padding: 24px;
        }

        .blp-title {
          font-size: 32px;
          font-weight: 700;
        }

        .blp-subtitle {
          font-size: 16px;
          opacity: 0.7;
          max-width: 400px;
        }

        .blp-buttons {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 16px;
        }

        .blp-btn {
          padding: 18px 48px;
          border: none;
          border-radius: 14px;
          font-size: 18px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.1s;
          min-width: 240px;
        }

        .blp-btn:hover:not(:disabled) {
          transform: scale(1.02);
        }

        .blp-btn:active:not(:disabled) {
          transform: scale(0.98);
        }

        .blp-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .blp-btn--primary {
          background: #22c55e;
          color: white;
          box-shadow: 0 4px 20px rgba(34, 197, 94, 0.4);
        }

        .blp-btn--primary:hover:not(:disabled) {
          background: #16a34a;
        }

        .blp-btn--secondary {
          background: #3b82f6;
          color: white;
          box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
        }

        .blp-btn--secondary:hover:not(:disabled) {
          background: #2563eb;
        }

        .blp-btn--cancel {
          background: #ef4444;
          color: white;
          box-shadow: 0 4px 20px rgba(239, 68, 68, 0.3);
        }

        .blp-btn--cancel:hover:not(:disabled) {
          background: #dc2626;
        }

        .blp-divider {
          display: flex;
          align-items: center;
          width: 100%;
          margin: 8px 0;
        }

        .blp-divider::before,
        .blp-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255, 255, 255, 0.2);
        }

        .blp-divider span {
          padding: 0 16px;
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
        }

        .blp-status {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          min-height: 24px;
          margin-top: 8px;
        }

        .blp-status.searching {
          color: #fbbf24;
        }

        .blp-status.found {
          color: #22c55e;
        }

        .blp-status.error {
          color: #ef4444;
        }

        .blp-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(251, 191, 36, 0.3);
          border-top-color: #fbbf24;
          border-radius: 50%;
          animation: blp-spin 1s linear infinite;
          margin-right: 8px;
          vertical-align: middle;
        }

        @keyframes blp-spin {
          to { transform: rotate(360deg); }
        }

        .hidden {
          display: none !important;
        }

        .blp-qr-section {
          margin-top: 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .blp-qr-label {
          font-size: 14px;
          opacity: 0.6;
        }

        .blp-qr-code {
          width: 156px;
          height: 156px;
          border-radius: 8px;
          background: white;
          padding: 8px;
        }

        .blp-greeting {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .blp-greeting-text {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.9);
        }

        .blp-greeting-name {
          font-weight: 600;
          color: #3b82f6;
        }

        .blp-edit-btn {
          background: none;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: rgba(255, 255, 255, 0.7);
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .blp-edit-btn:hover {
          border-color: rgba(255, 255, 255, 0.5);
          color: white;
          background: rgba(255, 255, 255, 0.1);
        }

        .blp-edit-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
      </style>

      <div class="blp-content">
        <div class="blp-greeting" id="blp-greeting">
          <span class="blp-greeting-text">Welcome, <span class="blp-greeting-name" id="blp-player-name">Player</span></span>
          <button class="blp-edit-btn" id="blp-edit-name-btn">Edit</button>
        </div>
        <div class="blp-title">Ready for Battle?</div>
        <div class="blp-subtitle">Enter the arena and fight for glory</div>

        <div class="blp-buttons">
          <button class="blp-btn blp-btn--primary" id="blp-single-player-btn">
            Play vs AI
          </button>

          <div class="blp-divider"><span>or</span></div>

          <button class="blp-btn blp-btn--secondary" id="blp-find-match-btn">
            Find Match
          </button>
          <button class="blp-btn blp-btn--cancel hidden" id="blp-cancel-search-btn">
            Cancel Search
          </button>
        </div>

        <div class="blp-status" id="blp-status"></div>

        <div class="blp-qr-section">
          <div class="blp-qr-label">Scan to play on another device</div>
          <img class="blp-qr-code" src="qr_code.png" alt="QR Code">
        </div>
      </div>
    `;

    this.container.appendChild(this.element);
    this.setupButtonHandlers();
  }

  private setupButtonHandlers(): void {
    const singlePlayerBtn = document.getElementById('blp-single-player-btn');
    const findMatchBtn = document.getElementById('blp-find-match-btn');
    const cancelSearchBtn = document.getElementById('blp-cancel-search-btn');
    const editNameBtn = document.getElementById('blp-edit-name-btn');

    singlePlayerBtn?.addEventListener('click', () => {
      this.onEnterBattle();
    });

    findMatchBtn?.addEventListener('click', () => {
      this.startSearch();
    });

    cancelSearchBtn?.addEventListener('click', () => {
      this.cancelSearch();
    });

    editNameBtn?.addEventListener('click', () => {
      this.openEditNameModal();
    });
  }

  private openEditNameModal(): void {
    // Don't allow editing during active multiplayer match
    if (this.gameController?.networkManager.isInGame()) {
      return;
    }
    this.playerNameModal?.show(true);
  }

  private updateGreeting(): void {
    const nameEl = document.getElementById('blp-player-name');
    if (nameEl && this.playerService) {
      nameEl.textContent = this.playerService.getName();
    }
  }

  private setupNetworkListeners(): void {
    if (!this.eventBus) return;

    // Listen for player name changes
    this.eventBus.on(PlayerEvents.NAME_CHANGED, () => {
      this.updateGreeting();
    });

    this.eventBus.on(NetworkEvents.CONNECTED, () => {
      this.updateStatus('Connected to server', '');
    });

    this.eventBus.on(NetworkEvents.DISCONNECTED, () => {
      this.clearQueueTimeout();
      this.updateStatus('Disconnected from server', 'error');
      this.showSearchButton();
    });

    this.eventBus.on<{ position: number }>(NetworkEvents.QUEUE_JOINED, (data) => {
      this.updateStatus(
        `<span class="blp-spinner"></span>Searching for opponent... (Position: ${data.position})`,
        'searching'
      );
    });

    this.eventBus.on<{ playerId: 1 | 2 }>(NetworkEvents.MATCH_FOUND, (data) => {
      this.clearQueueTimeout();
      this.updateStatus(`Match found! You are Player ${data.playerId}`, 'found');

      // Create game UI immediately
      this.onEnterBattle();

      // Hide panel after short delay
      setTimeout(() => {
        this.hide();
      }, 500);
    });

    this.eventBus.on<{ message: string }>(NetworkEvents.ERROR, (data) => {
      this.clearQueueTimeout();
      this.updateStatus(`Error: ${data.message}`, 'error');
      this.showSearchButton();
    });
  }

  private startSearch(): void {
    if (!this.gameController) return;

    const playerName = this.playerService?.getName();

    if (!this.gameController.networkManager.isConnected()) {
      this.updateStatus('Connecting to server...', '');
      this.gameController.connectToServer();

      const checkConnection = setInterval(() => {
        if (this.gameController?.networkManager.isConnected()) {
          clearInterval(checkConnection);
          this.gameController.joinMatchmaking(playerName);
          this.showCancelButton();
          this.startQueueTimeout();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkConnection);
        if (!this.gameController?.networkManager.isConnected()) {
          this.updateStatus('Failed to connect to server', 'error');
        }
      }, 5000);
    } else {
      this.gameController.joinMatchmaking(playerName);
      this.showCancelButton();
      this.startQueueTimeout();
    }
  }

  private cancelSearch(): void {
    if (!this.gameController) return;

    this.clearQueueTimeout();
    this.gameController.leaveMatchmaking();
    this.updateStatus('Search cancelled', '');
    this.showSearchButton();
  }

  private showSearchButton(): void {
    const findMatchBtn = document.getElementById('blp-find-match-btn');
    const cancelSearchBtn = document.getElementById('blp-cancel-search-btn');
    findMatchBtn?.classList.remove('hidden');
    cancelSearchBtn?.classList.add('hidden');
  }

  private showCancelButton(): void {
    const findMatchBtn = document.getElementById('blp-find-match-btn');
    const cancelSearchBtn = document.getElementById('blp-cancel-search-btn');
    findMatchBtn?.classList.add('hidden');
    cancelSearchBtn?.classList.remove('hidden');
  }

  private startQueueTimeout(): void {
    this.clearQueueTimeout();
    this.queueTimeoutId = setTimeout(() => {
      this.fallbackToAI();
    }, QUEUE_TIMEOUT_MS);
  }

  private clearQueueTimeout(): void {
    if (this.queueTimeoutId) {
      clearTimeout(this.queueTimeoutId);
      this.queueTimeoutId = null;
    }
  }

  private fallbackToAI(): void {
    // Leave the queue if still connected
    this.gameController?.leaveMatchmaking();

    // Update status to inform user
    this.updateStatus('No opponents found. Starting AI match...', 'found');

    // Short delay for user to see message, then start single player
    setTimeout(() => {
      this.onEnterBattle();
    }, 1000);
  }

  private updateStatus(text: string, className: string): void {
    const statusEl = document.getElementById('blp-status');
    if (statusEl) {
      statusEl.innerHTML = text;
      statusEl.className = 'blp-status';
      if (className) {
        statusEl.classList.add(className);
      }
    }
  }

  show(): void {
    if (this.element) {
      this.element.style.display = 'flex';
      // Reset state when showing
      this.clearQueueTimeout();
      this.showSearchButton();
      this.updateStatus('', '');
    }
  }

  hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  isVisible(): boolean {
    return this.element?.style.display !== 'none';
  }

  dispose(): void {
    this.clearQueueTimeout();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
